import { uploadImage } from './api-service.js';

/**
 * R2 Worker URL for assets
 */
const ASSETS_WORKER_URL = 'https://model-viewer-assets.adrian-a87.workers.dev';

/**
 * Initialize image upload handling
 * @param {HTMLElement} fileInput - The file input element
 * @param {HTMLElement} previewContainer - The preview container element
 * @param {HTMLElement} previewImage - The preview image element
 * @param {HTMLElement} removeButton - The remove image button element
 * @returns {Object} - Object with image handling methods
 */
export function initImageUpload(fileInput, previewContainer, previewImage, removeButton) {
    let currentImageUrl = null;
    let currentThumbnailUrl = null;
    let imageExplicitlyRemoved = false;

    // Set an empty src explicitly to prevent browser from trying to load base URL
    if (!previewImage.src || previewImage.src === window.location.origin + '/') {
        previewImage.src = '';
    }

    fileInput.addEventListener('change', async function(event) {
        if (event.target.files && event.target.files[0]) {
            let file = event.target.files[0];
            
            // Special handling for iOS devices
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            if (isIOS) {
                // Normalize file name to ensure it doesn't contain problematic characters
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const newFileName = `iphone-image-${Date.now()}.${fileExtension}`;
                
                // Create a new file object with the normalized name
                file = new File([file], newFileName, { type: file.type });
            }
            
            // Show loading state
            previewImage.src = '';
            previewContainer.classList.remove('hidden');
            previewContainer.classList.add('loading');
            
            // Try to upload to server
            const uploadResult = await uploadImage(file);
            
            if (uploadResult && uploadResult.imageUrl) {
                // If server upload was successful, use those URLs
                previewImage.src = uploadResult.imageUrl;
                currentImageUrl = uploadResult.imageUrl;
                
                // Ensure we never use base64 for thumbnails
                if (uploadResult.thumbnailUrl && uploadResult.thumbnailUrl.startsWith('data:image')) {
                    console.warn('Received base64 thumbnailUrl from server, using imageUrl instead');
                    currentThumbnailUrl = uploadResult.imageUrl;
                } else {
                    currentThumbnailUrl = uploadResult.thumbnailUrl || uploadResult.imageUrl;
                }
                
            } else {
                
                // Server upload failed - show error state
                console.error('Image upload failed');
                previewImage.src = '';
                currentImageUrl = null;
                currentThumbnailUrl = null;
                // Display a user-friendly error message
                previewContainer.classList.add('upload-error');
            }
            
            // Remove loading state
            previewContainer.classList.remove('loading');
            
            // Reset the flag when a new image is uploaded
            imageExplicitlyRemoved = false;
        }
    });
    
    removeButton.addEventListener('click', function() {
        // Don't set currentImageUrl to null here, it's removed in the main script
        previewImage.src = '';
        previewContainer.classList.add('hidden');
        fileInput.value = '';
        
        // Dispatch an event to notify that the image has been removed
        document.dispatchEvent(new CustomEvent('imageRemoved', { 
            detail: { timestamp: new Date().getTime() } 
        }));
        
        imageExplicitlyRemoved = true;
    });
    
    return {
        setCurrentImageUrl: function(imageUrl, thumbnailUrl = null) {
            currentImageUrl = imageUrl;
            
            // Ensure we never use base64 thumbnails
            if (thumbnailUrl && typeof thumbnailUrl === 'string' && thumbnailUrl.startsWith('data:image')) {
                console.warn('Detected base64 thumbnailUrl, using imageUrl instead');
                currentThumbnailUrl = imageUrl;
            } else {
                currentThumbnailUrl = thumbnailUrl || imageUrl;
            }
        },
        getCurrentImageUrl: function() {
            return currentImageUrl;
        },
        getCurrentThumbnailUrl: function() {
            return currentThumbnailUrl;
        },
        getProxiedImageUrl: function() {
            // If the image URL doesn't already use the worker, convert it
            if (currentImageUrl) {
                // Check if this is already a worker URL
                if (currentImageUrl.includes(ASSETS_WORKER_URL)) {
                    return currentImageUrl;
                }
                // Extract the filename/path from the URL and use with worker
                const pathMatch = currentImageUrl.match(/\/([^\/]+)$/);
                return pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : currentImageUrl;
            }
            return null;
        },
        resetImageUpload: function() {
            currentImageUrl = null;
            currentThumbnailUrl = null;
            previewImage.src = '';
            previewContainer.classList.add('hidden');
            fileInput.value = '';
            imageExplicitlyRemoved = false;
        },
        isImageExplicitlyRemoved: function() {
            return imageExplicitlyRemoved;
        },
        setImageExplicitlyRemoved: function(value) {
            imageExplicitlyRemoved = value;
        }
    };
}

/**
 * Create an image modal for viewing images
 * @param {string} imageUrl - URL of the image to display
 */
export function createImageModal(imageUrl) {
  
    // Validate URL before attempting to create the modal
    if (!isValidImageUrl(imageUrl)) {
        console.warn(`Invalid image URL for modal: ${imageUrl}`);
        return;
    }
    
    const modal = document.createElement('div');
    modal.classList.add('image-modal');
    
    const image = document.createElement('img');
    image.classList.add('image-modal-content');

    // Make sure crossOrigin is set before applying other fixes
    image.crossOrigin = 'anonymous';
    
    // Add loading indicator
    const loadingIndicator = document.createElement('div');
    loadingIndicator.textContent = 'Loading image...';
    loadingIndicator.style.color = 'white';
    loadingIndicator.style.textAlign = 'center';
    loadingIndicator.style.padding = '20px';
    loadingIndicator.style.backgroundColor = 'rgba(0,0,0,0.7)';
    loadingIndicator.style.borderRadius = '5px';
    loadingIndicator.style.position = 'absolute';
    loadingIndicator.style.top = '50%';
    loadingIndicator.style.left = '50%';
    loadingIndicator.style.transform = 'translate(-50%, -50%)';
    
    // Show loading indicator
    modal.appendChild(loadingIndicator);

    // Ensure the image URL uses the worker if needed
    let finalImageUrl = imageUrl;
    if (imageUrl && !imageUrl.includes(ASSETS_WORKER_URL) && !imageUrl.startsWith('data:')) {
        // Extract the filename/path from the URL and use with worker
        const pathMatch = imageUrl.match(/\/([^\/]+)$/);
        finalImageUrl = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : imageUrl;
    }
    
    // Set image src after adding CORS attributes
    image.src = finalImageUrl;
    
    // Hide loading indicator when image is loaded
    image.onload = function() {
        if (loadingIndicator.parentNode === modal) {
            modal.removeChild(loadingIndicator);
        }
    };

    // Handle image load failure
    image.onerror = function() {
        console.error('Modal image failed to load:', finalImageUrl);
        if (loadingIndicator.parentNode === modal) {
            loadingIndicator.textContent = 'Failed to load image';
            loadingIndicator.style.backgroundColor = 'rgba(255,0,0,0.7)';
        }
    };
    
    const closeButton = document.createElement('span');
    closeButton.classList.add('image-modal-close');
    closeButton.innerHTML = '&times;';
    
    modal.appendChild(image);
    modal.appendChild(closeButton);
    document.body.appendChild(modal);
    
    modal.addEventListener('click', function(event) {
        if (event.target === modal || event.target === closeButton) {
            document.body.removeChild(modal);
        }
    });
    
    // Log that the modal was created with the final URL
    console.log('Image modal created for:', finalImageUrl);
}

/**
 * Check if a URL is valid for image usage
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is valid
 */
function isValidImageUrl(url) {
    // Basic URL validation
    if (!url || url === '' || url === 'null') return false;
    
    // Check if URL is just the base origin with no path
    const origin = window.location.origin;
    if (url === origin || url === origin + '/') return false;
    
    // Check for standard image extensions (optional, might be too restrictive)
    // const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
    // return imageExtensions.test(url);
    return true;
}